'use client';

import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';

import { type FileInputErrorDetails, type TextAreaState } from '@videoblocks/design-system';
import { AiContentType } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentTypeEnum';

import { type StockItemResult } from '../../_shared/_actions/searchLibraryImages';
import { useAiToolkitContext } from '../../_shared/_context/AiToolkitContext';
import {
    validateVideoGenerationForm,
    VIDEO_GENERATION_FORM_DEFAULTS,
    type VideoGenerationFormErrors,
    type VideoGenerationFormState,
} from '../../_shared/_context/VideoGenerationFormState';
import { MAX_PROMPT_LENGTH, VIDEO_GENERATION_CREDIT_COST_PER_SECOND } from '../../_shared/_context/constants';
import { AI_TOOLKIT_CATEGORIES, type AiTaskSubmitRouteResponse } from '../../_shared/_context/types';
import { fileToDataUrl } from '../../_shared/_utils/fileToDataUrl';

const VIDEO_GENERATION_ENDPOINT = '/api/video-generation';

type StockblocksRatio = '1280:720' | '720:1280' | '1080:1920' | '1920:1080';

function aspectAndResolutionToRatio(aspect: string, resolution: string): StockblocksRatio {
    const isLandscape = aspect === '16:9';
    const is720 = resolution === '720p';
    if (isLandscape) return is720 ? '1280:720' : '1920:1080';
    return is720 ? '720:1280' : '1080:1920';
}

export type UseVideoGenerationFormReturn = {
    formState: VideoGenerationFormState;
    errors: VideoGenerationFormErrors;
    promptState: TextAreaState;
    promptHelperText: string | undefined;
    isValid: boolean;
    isSubmitDisabled: boolean;
    isDurationLocked: boolean;
    submitError: string | null;
    calculatedCost: number;
    onPromptChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    onPromptBlur: () => void;
    onAspectRatioChange: (value: string) => void;
    onDurationChange: (value: string) => void;
    onResolutionChange: (value: string) => void;
    onFileSelect: (file: File) => void;
    onFileRemove: () => void;
    onFileError: (details: FileInputErrorDetails) => string | void;
    onLibraryImageSelect: (item: StockItemResult) => void;
    onSubmit: () => void;
    onConfirm: () => Promise<void>;
    onClearSubmitError: () => void;
    resetForm: () => void;
};

export const useVideoGenerationForm = (): UseVideoGenerationFormReturn => {
    const {
        formStates,
        updateFormState,
        dispatchVideoGenerationSubmit,
        dispatchConfirmationModal,
        submitStates,
        refreshCredits,
        imageSearch,
        videoGenerationFileErrorMessage: fileErrorMessage,
        setVideoGenerationFileErrorMessage: setFileErrorMessage,
        videoGenerationSubmitError: submitError,
        setVideoGenerationSubmitError: setSubmitError,
    } = useAiToolkitContext();
    const { setShowSearch } = imageSearch;
    const videoSubmitState = submitStates[AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION];
    const isInProgress = videoSubmitState.status === 'inProgress';

    const formState = formStates[AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION] ?? VIDEO_GENERATION_FORM_DEFAULTS;
    const validationActive = formState.validationActive;

    useEffect(() => {
        if (!formStates[AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]) {
            updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION, VIDEO_GENERATION_FORM_DEFAULTS);
        }
    }, [formStates, updateFormState]);

    const { isValid, errors: schemaErrors } = useMemo(() => validateVideoGenerationForm(formState), [formState]);

    const isSubmitDisabled = validationActive && !isValid;

    const errors: VideoGenerationFormErrors = useMemo(() => {
        if (!validationActive) {
            return fileErrorMessage ? { file: fileErrorMessage } : {};
        }
        const merged = { ...schemaErrors };
        if (fileErrorMessage) {
            merged.file = fileErrorMessage;
        }
        return merged;
    }, [validationActive, schemaErrors, fileErrorMessage]);

    const promptState: TextAreaState = useMemo(() => {
        if (validationActive && schemaErrors.prompt) {
            return 'error';
        }
        return 'default';
    }, [validationActive, schemaErrors.prompt]);

    const calculatedCost = useMemo(
        () => Number(formState.duration) * VIDEO_GENERATION_CREDIT_COST_PER_SECOND,
        [formState.duration],
    );

    const promptHelperText = useMemo((): string | undefined => {
        if (validationActive && schemaErrors.prompt) {
            return schemaErrors.prompt;
        }
        if (formState.prompt.length >= MAX_PROMPT_LENGTH) {
            return `You've reached the maximum allowed length (${MAX_PROMPT_LENGTH.toLocaleString()} characters).`;
        }
        return undefined;
    }, [validationActive, schemaErrors.prompt, formState.prompt.length]);

    const updateField = useCallback(
        <K extends keyof VideoGenerationFormState>(field: K, value: VideoGenerationFormState[K]): void => {
            updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION, { ...formState, [field]: value });
        },
        [formState, updateFormState],
    );

    const onPromptChange = useCallback(
        (e: ChangeEvent<HTMLTextAreaElement>): void => {
            updateField('prompt', e.target.value);
        },
        [updateField],
    );

    const onPromptBlur = useCallback((): void => {
        updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION, { ...formState, validationActive: true });
    }, [formState, updateFormState]);

    const onAspectRatioChange = useCallback(
        (value: string): void => {
            updateField('aspectRatio', value);
        },
        [updateField],
    );

    const onDurationChange = useCallback(
        (value: string): void => {
            updateField('duration', value);
        },
        [updateField],
    );

    const onResolutionChange = useCallback(
        (value: string): void => {
            // 1080p video generations are restricted to 8s duration only (Runway limitation)
            if (value === '1080p') {
                updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION, {
                    ...formState,
                    resolution: value,
                    duration: '8',
                });
            } else {
                updateField('resolution', value);
            }
        },
        [formState, updateFormState, updateField],
    );

    const onFileSelect = useCallback(
        (file: File): void => {
            setFileErrorMessage(null);
            updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION, { ...formState, file, selectedLibraryImage: null });
        },
        [formState, updateFormState],
    );

    const onFileRemove = useCallback((): void => {
        setFileErrorMessage(null);
        updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION, {
            ...formState,
            file: null,
            selectedLibraryImage: null,
        });
    }, [formState, updateFormState]);

    const onLibraryImageSelect = useCallback(
        (item: StockItemResult): void => {
            setFileErrorMessage(null);
            updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION, {
                ...formState,
                file: null,
                selectedLibraryImage: {
                    thumbnailUrl: item.thumbnailUrl,
                    contentId: item.contentId,
                    title: item.title,
                },
            });
        },
        [formState, updateFormState, setFileErrorMessage],
    );

    const onFileError = useCallback(
        (details: FileInputErrorDetails): string | void => {
            const { type } = details;
            updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION, {
                ...formState,
                file: null,
                selectedLibraryImage: null,
            });
            if (type === 'unsupported_type') {
                setFileErrorMessage('Unsupported file type. Please upload a JPEG, PNG, or WebP image.');
            } else if (type === 'file_too_large') {
                setFileErrorMessage('File exceeds the 15MB limit. Please upload a smaller image.');
            } else if (type === 'processing_error') {
                setFileErrorMessage('Upload failed. Please try again.');
            } else {
                setFileErrorMessage(details.defaultMessage);
            }
        },
        [formState, updateFormState],
    );

    const onSubmit = useCallback((): void => {
        updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION, { ...formState, validationActive: true });

        if (!isValid) {
            return;
        }

        dispatchConfirmationModal({
            type: 'OPEN_MODAL',
            modal: { type: 'creditConfirmation', category: AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION },
        });
    }, [formState, updateFormState, isValid, dispatchConfirmationModal]);

    const onClearSubmitError = useCallback((): void => {
        setSubmitError(null);
    }, []);

    const onConfirm = useCallback(async (): Promise<void> => {
        if (isInProgress) return;
        setShowSearch(false);
        dispatchVideoGenerationSubmit({ type: 'GENERATION_IN_PROGRESS' });
        dispatchConfirmationModal({ type: 'CLOSE_MODAL' });
        setSubmitError(null);

        // Capture the content type and model at submit time. Reading them later from live form
        // state would misreport the generation if the user edits the form afterward (e.g. removes
        // the start frame), so they are frozen into submit state and carried to the events.
        const usesStartFrame = !!formState.file || !!formState.selectedLibraryImage;
        const aiContentType = usesStartFrame ? AiContentType.IMAGE_TO_VIDEO : AiContentType.TEXT_TO_VIDEO;
        const aiModelName = formState.model;

        try {
            const duration = Number(formState.duration) as 4 | 6 | 8;
            const ratio = aspectAndResolutionToRatio(formState.aspectRatio, formState.resolution);
            const trimmedPrompt = formState.prompt.trim();
            const sharedBody = {
                prompt: trimmedPrompt || undefined,
                algoParams: { modelId: formState.model, ratio, duration, audio: false },
            };
            let body;
            if (formState.file) {
                body = {
                    taskType: 'image_to_video' as const,
                    imageUrl: await fileToDataUrl(formState.file),
                    ...sharedBody,
                };
            } else if (formState.selectedLibraryImage) {
                body = {
                    taskType: 'image_to_video' as const,
                    contentId: formState.selectedLibraryImage.contentId,
                    ...sharedBody,
                };
            } else {
                body = {
                    taskType: 'text_to_video' as const,
                    ...sharedBody,
                };
            }

            const response = await fetch(VIDEO_GENERATION_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            void refreshCredits();

            const payload = (await response.json()) as AiTaskSubmitRouteResponse;

            if (!response.ok || payload.success !== true) {
                const message =
                    'message' in payload && payload.message
                        ? payload.message
                        : 'error' in payload && payload.error
                          ? payload.error
                          : 'Video generation failed. Please try again.';
                const failureReason = 'failureReason' in payload ? payload.failureReason ?? null : null;
                setSubmitError(message);
                dispatchVideoGenerationSubmit({ type: 'GENERATION_ERROR', failureReason });
                return;
            }

            dispatchVideoGenerationSubmit({
                type: 'GENERATION_IN_PROGRESS',
                pendingTaskId: payload.taskId,
                aiContentId: payload.aiContentId,
                lastAiContentId: payload.lastAiContentId,
                aiContentType,
                aiModelName,
            });
        } catch (err) {
            // TODO: handle credit refund?
            void refreshCredits();
            setSubmitError(err instanceof Error ? err.message : 'Video generation failed. Please try again.');
            dispatchVideoGenerationSubmit({ type: 'GENERATION_ERROR' });
        }
    }, [
        dispatchConfirmationModal,
        dispatchVideoGenerationSubmit,
        formState,
        isInProgress,
        refreshCredits,
        setShowSearch,
    ]);

    const resetForm = useCallback((): void => {
        updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION, VIDEO_GENERATION_FORM_DEFAULTS);
        setFileErrorMessage(null);
        dispatchConfirmationModal({ type: 'CLOSE_MODAL' });
        setSubmitError(null);
        dispatchVideoGenerationSubmit({ type: 'RESET' });
    }, [dispatchConfirmationModal, dispatchVideoGenerationSubmit, updateFormState]);

    const isDurationLocked = formState.resolution === '1080p';

    return {
        formState,
        errors,
        promptState,
        promptHelperText,
        isValid,
        isSubmitDisabled,
        isDurationLocked,
        submitError,
        calculatedCost,
        onPromptChange,
        onPromptBlur,
        onAspectRatioChange,
        onDurationChange,
        onResolutionChange,
        onFileSelect,
        onFileRemove,
        onFileError,
        onLibraryImageSelect,
        onSubmit,
        onConfirm,
        onClearSubmitError,
        resetForm,
    };
};
